import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import { CardMode } from '@components/card/card.component';
import { MdsCheckboxComponent } from '@medpacesoftwaredevelopment/designsystem';
import { Caregiver } from '@models/patient';
import { TravelViewModelService } from '@services/travel-view-model/travel-view-model.service';
import { filter, map, merge, Observable, pairwise, startWith, switchMap, tap } from 'rxjs';
import { AccompanyingCaregiversFormGroup } from '../form-groups/travel-request-card.formgroup';
import { CaregiverCheckboxOptionEditComponent } from './caregiver-checkbox-option-edit/caregiver-checkbox-option-edit.component';
import { CaregiverCheckboxOptionViewComponent } from './caregiver-checkbox-option-view/caregiver-checkbox-option-view.component';

@Component({
    selector: 'medpace-caregiver-checkbox-group',
    imports: [
        CommonModule,
        MdsCheckboxComponent,
        AlertComponent,
        CaregiverCheckboxOptionEditComponent,
        CaregiverCheckboxOptionViewComponent,
    ],
    templateUrl: './caregiver-checkbox-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CaregiverCheckboxGroupComponent {
    private readonly travelViewModelService = inject(TravelViewModelService);
    public readonly formGroup: InputSignal<AccompanyingCaregiversFormGroup> = input.required();
    public readonly mode: InputSignal<CardMode> = input.required();

    public readonly bookViaGetThereForCaregiver: OutputEmitterRef<Caregiver> = output<Caregiver>();

    private readonly formGroup$ = toObservable(this.formGroup);

    private readonly noneCaregiverValueChanges$: Observable<AccompanyingCaregiversFormGroup> = this.formGroup$.pipe(
        switchMap(
            (formGroup: AccompanyingCaregiversFormGroup) => formGroup.controls.none.controls.checked.valueChanges
        ),
        filter(Boolean),
        switchMap(() => this.formGroup$),
        tap((formGroup: AccompanyingCaregiversFormGroup) => {
            formGroup.controls.primaryCaregiver.controls.checked.setValue(false);
            formGroup.controls.otherCaregivers.controls.forEach((control) => control.controls.checked.setValue(false));
        })
    );

    private readonly anyCaregiverValueChanges$ = this.formGroup$.pipe(
        switchMap((formGroup) =>
            merge(
                formGroup.controls.primaryCaregiver.controls.checked.valueChanges.pipe(
                    startWith(formGroup.getRawValue().primaryCaregiver.checked),
                    pairwise()
                ),
                ...formGroup.controls.otherCaregivers.controls.map((c) =>
                    c.controls.checked.valueChanges.pipe(startWith(c.getRawValue().checked), pairwise())
                )
            )
        ),
        filter(([prev, curr]) => prev !== curr),
        switchMap(() => this.formGroup$),
        tap((formGroup: AccompanyingCaregiversFormGroup) => {
            const formValue = formGroup.getRawValue();
            const newNoneCaregiverValue = ![
                formValue.primaryCaregiver.checked && !!formValue.primaryCaregiver.value?.caregiver,
                ...formValue.otherCaregivers.map((c) => c.checked),
            ].includes(true);

            formGroup.controls.none.controls.checked.setValue(newNoneCaregiverValue);
        })
    );

    public readonly handleCaregiverChange$ = merge(
        this.noneCaregiverValueChanges$,
        this.anyCaregiverValueChanges$,
        this.formGroup$
    ).pipe(
        map((formGroup: AccompanyingCaregiversFormGroup) => {
            const value = formGroup.getRawValue();
            if (value.none.checked) return [];
            else
                return [value.primaryCaregiver, ...value.otherCaregivers]
                    .filter((c) => c.checked)
                    .map((c) => c.value.caregiver);
        }),
        tap((caregivers) => {
            const travelFormGroup = this.travelViewModelService.travelFormGroup();
            if (travelFormGroup) {
                const accompanyingCaregiverTravelControls = [
                    ...travelFormGroup.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.caregivers.controls.filter(
                        (caregiverControl) =>
                            caregivers.some((caregiver) => caregiver.id === caregiverControl.getRawValue().caregiver.id)
                    ),
                    ...travelFormGroup.controls.travelDetails.controls.train.controls.travel.controls.requestDetails.controls.caregivers.controls.filter(
                        (caregiverControl) =>
                            caregivers.some((caregiver) => caregiver.id === caregiverControl.getRawValue().caregiver.id)
                    ),
                ];
                const nonAccompanyingCaregiverTravelControls = [
                    ...travelFormGroup.controls.travelDetails.controls.flight.controls.travel.controls.requestDetails.controls.caregivers.controls.filter(
                        (caregiverControl) =>
                            !caregivers.some(
                                (caregiver) => caregiver.id === caregiverControl.getRawValue().caregiver.id
                            )
                    ),
                    ...travelFormGroup.controls.travelDetails.controls.train.controls.travel.controls.requestDetails.controls.caregivers.controls.filter(
                        (caregiverControl) =>
                            !caregivers.some(
                                (caregiver) => caregiver.id === caregiverControl.getRawValue().caregiver.id
                            )
                    ),
                ];
                accompanyingCaregiverTravelControls.forEach((control) =>
                    control.enable({ breakPersistence: true, onlySelf: true, emitEvent: false })
                );
                nonAccompanyingCaregiverTravelControls.forEach((control) =>
                    control.disable({ persistent: true, onlySelf: true, emitEvent: false })
                );
            }
        })
    );
}
